<template>
    <el-form :model="form.data" ref="videoForm">

        <div class="row">
            <div class="col-lg-5 col-xl-3 mt-7 mt-md-0">
                <div class="card">
                    <div class="card-body d-flex flex-column p-9">
                        <div class="fv-row mb-6">
                            <label class="fs-6 required fw-bold mb-4">{{ $t('pages.video.upload.cols.group') }}:</label>
                            <el-form-item prop="group_id" :rules="$validation.getMessage(['required'])">
                                <el-select v-model="form.data.group_id" :placeholder="$t('common.chooseSelect')" filterable class="w-100">
                                    <el-option v-for="(group, groupIndex) in videoGroups" :key="groupIndex" :value="group.id" :label="group.name"></el-option>
                                </el-select>
                            </el-form-item>
                        </div>
                        <div class="fv-row">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.video.upload.cols.option') }}:</label>
                            <div>
                                <div class="form-check form-check-custom form-check-solid my-2">
                                    <input class="form-check-input" type="checkbox" v-model="form.data.convert_hls" id="exportHLS">
                                    <label class="form-check-label" for="exportHLS">
                                        {{$t('pages.video.upload.option.convertHls') }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <div class="col-lg-7 col-xl-9">
                <div class="card">
                    <div class="card-body d-flex flex-column p-9">
                        <div class="fv-row">
                            <label class="required fs-6 fw-bold mb-2">{{ $t('pages.video.upload.cols.video.title') }}:</label>
                            <el-form-item prop="file">
                                <el-upload :file-list="form.fileList" accept="video/*" class="upload-file mt-2" :show-file-list="false" :on-change="handleChangeFile" :on-remove="handleChangeFile" :auto-upload="false" drag multiple :limit="100">
                                    <el-icon class="el-icon--upload">
                                         <span class="svg-icon">
                                             <inline-svg src="/media/icons/duotune/files/fil022.svg" />
                                         </span>
                                    </el-icon>
                                    <div class="el-upload__text">{{ $t('pages.video.upload.cols.video.dragFile')}}</div>
                                </el-upload>
                            </el-form-item>
                        </div>

                        <div v-if="form.fileList.length" class="mt-6">
                            <div class="d-flex align-items-center justify-content-between mb-3">
                                <span class="fs-6 fw-semibold text-gray-700">
                                    {{ sprintf($t('pages.video.upload.filesSelected'), [form.fileList.length]) }}
                                    <span class="text-gray-400 mx-1">·</span>
                                    <span class="text-gray-500">{{ sprintf($t('pages.video.upload.totalSize'), [uploadTotalSize]) }}</span>
                                </span>
                                <el-button v-if="!form.loading" type="danger" link size="small" @click="clearUploadList">
                                    {{ $t('pages.video.upload.clearAll') }}
                                </el-button>
                            </div>

                            <div class="upload-file-list">
                                <div v-for="(file, fileIndex) in form.fileList" :key="file.uid ?? fileIndex" class="upload-file-item d-flex align-items-center position-relative px-4">
                                    <span class="badge badge-circle badge-light-primary flex-shrink-0 me-4">{{ fileIndex + 1 }}</span>

                                    <el-input v-model="file.name" :disabled="form.loading" type="text" class="flex-grow-1"/>

                                    <span class="upload-file-size text-gray-400 fs-7 text-nowrap text-start flex-shrink-0 ms-4">{{ formatBytes(file.size || (file.raw && file.raw.size)) }}</span>

                                    <span v-if="file.uploadStatus" class="badge flex-shrink-0 ms-4" :class="uploadStatusClass(file.uploadStatus)">
                                        {{ uploadStatusText(file.uploadStatus) }}
                                    </span>

                                    <button v-if="!form.loading" type="button" class="btn btn-icon btn-sm rounded-circle w-30px h-30px flex-shrink-0 ms-4 upload-file-remove" @click="removeUploadFile(fileIndex)">
                                        <i class="bi bi-x fs-2"></i>
                                    </button>

                                    <div v-if="file.uploadStatus === 'uploading'" class="upload-file-progress" :style="{ width: (file.uploadProgress || 0) + '%' }"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card-footer d-flex justify-content-end py-6 px-9">
                        <button @click.prevent="onSubmit" :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="button">
                            <span v-if="!form.loading" class="indicator-label">{{ $t("pages.video.upload.submit") }}</span>
                            <span v-if="form.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </el-form>
</template>

<script>
export default {
    name: "index",
    data(){
        return {
            form: {
                loading: false,
                fileList: [],
                uploadList: [],
                data: {
                    convert_unsupported: true,
                    convert_hls: true
                },
            },
            UPLOAD_CONCURRENCY: 3
        }
    },
    computed: {
        videoGroups() {
            return this.$store.state.video.group.table.data;
        },
        uploadTotalSize() {
            let bytes = this.form.fileList.reduce((sum, file) => {
                return sum + (file.size || (file.raw && file.raw.size) || 0);
            }, 0);

            return this.formatBytes(bytes);
        },
    },
    mounted(){
        this.setCurrentPageBreadcrumbs(this.$t("pages.video.upload.title"), [this.$t("menu.contentManagement.title"), this.$t("menu.contentManagement.video.title")]);
        this.$store.dispatch('video/group/get', {
            page: { pageSize: 9999 }
        });
    },
    methods: {
        onSubmit(){
            this.$refs.videoForm.validate(async (valid) => {
                if(!valid){
                    return false;
                }

                if(!(this.form.fileList.length > 0)){
                    this.$notify({
                        type: 'warning',
                        title: this.$t("messages.warning"),
                        message: this.$t("pages.video.upload.warnings.requiredFile"),
                    })
                    return false;
                }

                this.form.loading = true;

                let pending = this.form.fileList.filter((file) => file.uploadStatus !== 'success');

                pending.forEach((file) => {
                    file.uploadStatus = 'pending';
                    file.uploadProgress = 0;
                });

                let results = await this.runUploadQueue(pending, this.UPLOAD_CONCURRENCY);
                let failed = results.filter((result) => !result.success);

                this.form.loading = false;

                if(failed.length === 0){
                    this.$notify({
                        type: 'success',
                        title: this.$t("messages.success"),
                        message: this.sprintf(this.$t("pages.video.upload.messages.allSuccess"), [pending.length]),
                    });

                    setTimeout(() => {
                        this.$router.push({path: "/video"});
                    }, 500);
                } else {
                    this.$notify({
                        type: 'error',
                        title: this.$t("messages.error"),
                        message: this.sprintf(this.$t("pages.video.upload.messages.partialFail"), [failed.length]),
                    });
                }
            });
        },
        async runUploadQueue(files, concurrency){
            let results = new Array(files.length);
            let cursor = 0;

            let worker = async () => {
                while(cursor < files.length){
                    let index = cursor++;
                    results[index] = await this.uploadSingleFile(files[index]);
                }
            };

            let pool = Array.from(
                { length: Math.min(concurrency, files.length) },
                () => worker()
            );

            await Promise.all(pool);

            return results;
        },
        async uploadSingleFile(file){
            file.uploadStatus = 'uploading';
            file.uploadProgress = 0;

            const formData = new FormData();
            formData.append("file", file.raw);
            formData.append("title", file.name);

            for (let key in this.form.data){
                formData.append(key, (typeof(this.form.data[key]) == 'boolean' ? (Number(this.form.data[key])) : this.form.data[key]));
            }

            try {
                let response = await this.axios.post(this.endpoint.get('video'), formData, {
                    onUploadProgress: (event) => {
                        if(event.total){
                            file.uploadProgress = Math.round((event.loaded * 100) / event.total);
                        }
                    }
                });

                if(response.data && response.data.status){
                    file.uploadStatus = 'success';
                    file.uploadProgress = 100;

                    return { success: true, data: response.data.data };
                }

                file.uploadStatus = 'error';
                return { success: false };
            } catch (err) {
                file.uploadStatus = 'error';

                this.axios.post(this.endpoint.get('log_register', 'system'), {
                    error: err.response,
                    upload_content: { name: file.name, size: file.size }
                });

                return { success: false };
            }
        },
        uploadStatusText(status){
            return {
                pending: this.$t('pages.video.upload.status.pending'),
                uploading: this.$t('pages.video.upload.status.uploading'),
                success: this.$t('pages.video.upload.status.success'),
                error: this.$t('pages.video.upload.status.error'),
            }[status] || '';
        },
        uploadStatusClass(status){
            return {
                pending: 'badge-light-secondary',
                uploading: 'badge-light-primary',
                success: 'badge-light-success',
                error: 'badge-light-danger',
            }[status] || 'badge-light';
        },
        removeUploadFile(index){
            this.form.fileList.splice(index, 1);
            this.form.uploadList = this.form.fileList;
        },
        clearUploadList(){
            this.form.fileList = [];
            this.form.uploadList = [];
        },
        formatBytes(bytes){
            if(!bytes){
                return '0 B';
            }

            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));

            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        handleChangeFile(file, fileList){
            this.form.fileList = fileList;
            this.form.uploadList = fileList;
        },
    }
}
</script>

<style>
.upload-file .el-upload {
    width: 100%;
}

.upload-file .el-upload .el-upload-dragger{
    width: 100%;
}

.upload-file .svg-icon svg {
    height: 1em;
    width: 1em;
}
</style>

<style scoped>
.upload-file-list {
    max-height: 440px;
    overflow-y: auto;
    overflow-x: hidden;
    border: 1px solid #eff2f5;
    border-radius: .475rem;
}

.upload-file-item {
    height: 64px;
    border-bottom: 1px solid #f4f6f8;
    transition: background-color .15s ease;
}

.upload-file-item:last-child {
    border-bottom: none;
}

.upload-file-item:hover {
    background-color: #f9fafb;
}

/* boyut metni sabit genişlikte; böylece tüm input'lar aynı hizada biter */
.upload-file-size {
    width: 90px;
}

.upload-file-remove {
    background-color: #fbd9e1 !important;
    transition: background-color .15s ease, color .15s ease;
}

/* kart hover'da bile belirgin kalsın diye net kırmızı ikon */
.upload-file-remove i {
    color: #f1416c !important;
}

.upload-file-remove:hover {
    background-color: var(--bs-danger, #f1416c) !important;
}

.upload-file-remove:hover i {
    color: #fff !important;
}

.upload-file-progress {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    border-radius: 2px;
    background-color: var(--bs-primary, #009ef7);
    transition: width .25s ease;
}
</style>